import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { changeLanguageSafe, langMeta } from "@/i18n";
import globalI18n from "@/i18n";
import { isEnglishPath, toEnglishPath } from "@/lib/route-i18n";
import {
  detectBrowserLang,
  dismissSuggestion,
  getStoredLang,
  isSuggestionDismissed,
  savePreferredLangToAccount,
  setStoredLang,
} from "@/lib/lang-preference";

/** Texter på det föreslagna språket – behöver inte ligga i i18n-bundlarna. */
const COPY: Record<string, { prompt: string; action: string; close: string }> = {
  en: { prompt: "Prefer English?", action: "Switch to English", close: "Dismiss" },
  ar: { prompt: "تفضل العربية؟", action: "التبديل إلى العربية", close: "إغلاق" },
  fa: { prompt: "فارسی را ترجیح می‌دهید؟", action: "تغییر به فارسی", close: "بستن" },
  tr: { prompt: "Türkçe mi tercih edersiniz?", action: "Türkçeye geç", close: "Kapat" },
  ku: { prompt: "Kurdî tercîh dikî?", action: "Biguhere bo Kurdî", close: "Dade" },
  so: { prompt: "Ma doorbidaa Soomaali?", action: "U beddel Soomaali", close: "Xir" },
  th: { prompt: "ต้องการภาษาไทยไหม?", action: "เปลี่ยนเป็นภาษาไทย", close: "ปิด" },
};

export function LanguageSuggestionBanner() {
  const [suggest, setSuggest] = useState<string | null>(null);
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    // Endast för besökare utan eget val och som inte redan stängt rutan.
    if (getStoredLang() || isSuggestionDismissed()) return;
    const detected = detectBrowserLang();
    if (!detected || detected === "sv") return;
    if (detected === "en" && isEnglishPath(pathname)) return;
    if (!COPY[detected]) return;
    setSuggest(detected);
    // Kör bara en gång efter mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!suggest) return null;
  const copy = COPY[suggest];
  const meta = langMeta(suggest);

  const accept = () => {
    setStoredLang(suggest);
    void savePreferredLangToAccount(suggest);
    setSuggest(null);
    if (suggest === "en") {
      const target = toEnglishPath(pathname);
      globalI18n.changeLanguage("en");
      if (target !== pathname && !isEnglishPath(pathname)) {
        void router.navigate({ to: target, search: (prev: unknown) => prev as never });
      }
      return;
    }
    void changeLanguageSafe(suggest);
  };

  const close = () => {
    dismissSuggestion();
    setSuggest(null);
  };

  return (
    <div
      dir={meta.dir}
      className="border-b border-border bg-secondary/60 px-4 py-2 text-sm"
    >
      <div className="mx-auto flex max-w-5xl items-center justify-center gap-3">
        <span className="text-foreground">{copy.prompt}</span>
        <button
          type="button"
          onClick={accept}
          className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
        >
          {copy.action}
        </button>
        <button
          type="button"
          onClick={close}
          aria-label={copy.close}
          className="ms-auto rounded-md p-1 text-muted-foreground hover:bg-secondary"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
